import type { SmsInvite } from "@rhema/client";
import { Button, cn } from "@rhema/ui";
import { useEffect, useState } from "react";
import { useFeatures } from "../session/features";
import { useClient, useClientState } from "../session/session";
import { InviteSheet } from "./InviteSheet";

/** The number as the server keeps it: its country code and last digits. */
export function maskedNumber(invite: SmsInvite): string {
  const code = invite.country_code ? `+${invite.country_code} ` : "";
  return invite.last_digits ? `${code}••• ${invite.last_digits}` : "a phone number";
}

const DELIVERY: Record<SmsInvite["delivery"], string> = {
  sent: "Sent",
  delivered: "Delivered",
  failed: "Text not delivered",
};

/**
 * The invites the server texted for you in the last month, and whether each text arrived. A text
 * Twilio accepted can still fail to reach the phone, and the list says so when it does.
 */
export function TextedInvites({ className }: { className?: string }) {
  const client = useClient();
  const { smsInvites } = useClientState();
  const features = useFeatures();
  const on = features?.sms_invites === true;
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (on) void client.loadSmsInvites().catch(() => undefined);
  }, [on, client]);

  if (!on || !smsInvites?.length) return null;
  return (
    <section aria-labelledby="texted-invites" className={cn("px-5", className)}>
      <h3 id="texted-invites" className="m-0 mt-4 text-[15px] font-bold">
        Texted invites
      </h3>
      <ul aria-label="Texted invites" className="m-0 mt-1 list-none p-0">
        {smsInvites.map((invite) => {
          const failed = invite.delivery === "failed";
          return (
            <li key={invite.id} className="flex items-start gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="m-0 truncate text-[15px] font-semibold">Invite to {maskedNumber(invite)}</p>
                <p
                  className={cn(
                    "m-0 text-[13.5px]",
                    failed ? "font-semibold text-scarlet" : "text-ink-secondary",
                  )}
                >
                  {DELIVERY[invite.delivery]}
                </p>
                {failed && (
                  <p className="m-0 mt-0.5 text-[13px] text-ink-secondary">
                    Send the invite link another way, such as WhatsApp.
                  </p>
                )}
              </div>
              {failed && (
                <Button size="sm" variant="soft" onClick={() => setResending(true)}>
                  Send the link
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      <InviteSheet open={resending} onClose={() => setResending(false)} />
    </section>
  );
}
